import type { BlockItem, BlockWidget } from '@trinity/chat/types/dashboard-config';

/**
 * The pixel height of one widget row unit — the space each block row occupies in RGL.
 * Every SlotGrid container uses this as its per-row height budget.
 *
 * 380 px was chosen so that the usable area inside a 1×1 widget cell matches the
 * 320 px fixed canvas height used by the Nivo chart components:
 *   380 (ROW_HEIGHT) − 16 (SLOT_PADDING × 2) − 28 (SlotWidgetHeader) − 16 (container padding × 2) ≈ 320 px
 */
export const ROW_HEIGHT = 380;

/**
 * Space (px) between blocks, both ways. Vertically it's baked into each RGL item's height (RGL runs
 * in pixel mode with no vertical margin); horizontally it's RGL's column margin.
 */
export const BLOCK_GAP = 10;

/**
 * Pixel height of a collapsed block — just the Shell header.
 * ShellHeader padding (14+8 top/bottom) + ~34 px content line + 2 px Shell border + 6 px slack.
 */
export const SHELL_COLLAPSED_HEIGHT = 64;

/**
 * Padding (px) on each SlotGrid side (top + bottom). Derived from theme.spacing(1) = 8px.
 * Track heights inside SlotGrid = ROW_HEIGHT - 2 * SLOT_PADDING so items
 * fill their tracks exactly without overflowing the padded container.
 */
export const SLOT_PADDING = 8;

/**
 * Space (px) between the widgets of a cell, in rows and columns, while editing and viewing, so the
 * heights pinned while dragging add up to what's shown.
 */
export const CELL_GAP = 10;

/**
 * Smallest useful height (px) of a widget stacked beside a taller one. A widget dragged this much
 * taller than one row (plus the gap) leaves room for another widget beside it.
 */
export const STACK_ROW_HEIGHT = 120;

/** Maximum widget row span allowed in dashboard builder interactions. */
export const MAX_PERMISSIBLE_ROW_SPAN = 6;

/**
 * Minimum pixel width for a single outer column in the page renderer.
 * When the grid is wider than the viewport (high column count), the renderer
 * scrolls horizontally rather than compressing columns below this threshold.
 */
export const MIN_COL_WIDTH = 280;

/**
 * Pixel height of the WidgetCardShell drag-handle header.
 * ShellHeader: padding 14px top + 8px bottom = 22px; content (icon row) ≈ 26px → ~48px total.
 * This must be added to the RGL block height so the header doesn't steal
 * pixels from the slot grid rows.
 */
export const SHELL_HEADER_HEIGHT = 48;

/**
 * Pixel height of the WidgetCardShell header when hideTitle=true.
 * Matches the SectionHeader height — a compact 24 px bar styled like a section title.
 */
export const HIDDEN_TITLE_HEADER_HEIGHT = 24;

/**
 * Collapsed height for a block with hideTitle=true — just the compact header + gap slack.
 */
export const HIDDEN_TITLE_COLLAPSED_HEIGHT = HIDDEN_TITLE_HEADER_HEIGHT + 12;

/** Minimal structural shape of a section that the layout utils need. */
type SectionLike = {
  widgets: BlockWidget[];
  minInnerRows?: number | undefined;
};

/**
 * Returns the effective number of inner rows a section occupies.
 * Accounts for section.minInnerRows (drag-resize) and auto-derived row count from widgets.
 *
 * @param section  The section config (or any SectionLike)
 * @param maxCols  Max inner grid columns = the section's span in the parent block (1–3)
 */
export function getSectionEffectiveRows(section: SectionLike, maxCols: number): number {
  const { widgets, minInnerRows } = section;
  if (widgets.length === 0) return Math.max(minInnerRows ?? 1, 1);

  const maxRow = Math.max(...widgets.map((w: BlockWidget): number => w.row ?? 1));
  const lastRowWidgets = widgets.filter(
    (w: BlockWidget): boolean => (w.row ?? 1) === maxRow && w.slot <= maxCols,
  );
  const covered = lastRowWidgets.reduce<number>(
    (sum: number, w: BlockWidget): number =>
      sum + Math.min(w.span, maxCols - w.slot + 1),
    0,
  );
  const derived = covered >= maxCols ? maxRow + 1 : Math.max(1, maxRow);
  return Math.max(minInnerRows ?? 1, derived);
}

/**
 * Returns the number of inner rows a section occupies in PREVIEW / read-only mode.
 *
 * Unlike `getSectionEffectiveRows`, this omits the trailing empty row added for
 * drag-drop targets and the `minInnerRows` padding added by drag-resize.
 * In preview there are no interactions, so only actual widget content rows matter.
 */
export function getSectionPreviewRows(section: SectionLike, maxCols: number): number {
  const { widgets } = section;
  if (widgets.length === 0) return 1;
  const colFilteredWidgets = widgets.filter((w: BlockWidget): boolean => w.slot <= maxCols);
  if (colFilteredWidgets.length === 0) return 1;
  return Math.max(...colFilteredWidgets.map((w: BlockWidget): number => w.row ?? 1));
}

/**
 * Returns a Map<blockRowIndex, heightUnits> for each occupied block row.
 *
 * A block row that holds only widgets has heightUnits = 1,
 * unless a widget has rowSpan > 1 (then that widget contributes rowSpan units).
 * A block row that holds a section has heightUnits = getSectionEffectiveRows(section, span).
 * If a block row holds multiple items the tallest wins.
 */
export function getBlockRowHeightMap(items: BlockItem[], blockCols: number): Map<number, number> {
  if (items.length === 0) return new Map([[1, 1]]);

  const maxRow = Math.max(...items.map(i => i.row ?? 1));
  const map = new Map<number, number>();

  for (let r = 1; r <= maxRow; r++) {
    const rowItems = items.filter(i => (i.row ?? 1) === r);
    let h = 1;
    for (const item of rowItems) {
      if (item.type === 'section') {
        const maxCols = Math.max(1, Math.min(blockCols, item.span));
        h = Math.max(h, getSectionEffectiveRows(item.section, maxCols));
      } else if (item.type === 'widget' && (item.rowSpan ?? 1) > 1) {
        h = Math.max(h, item.rowSpan ?? 1);
      }
      else {
        //noop
      }
    }
    map.set(r, h);
  }

  return map;
}

/**
 * Like `getBlockRowHeightMap` but for PREVIEW / read-only mode.
 * Uses `getSectionPreviewRows` so section rows are not inflated by trailing
 * empty rows or `minInnerRows` padding.
 */
export function getPreviewBlockRowHeightMap(items: BlockItem[], blockCols: number): Map<number, number> {
  if (items.length === 0) return new Map([[1, 1]]);

  const maxRow = Math.max(...items.map(i => i.row ?? 1));
  const map = new Map<number, number>();

  for (let r = 1; r <= maxRow; r++) {
    const rowItems = items.filter(i => (i.row ?? 1) === r);
    let h = 1;
    for (const item of rowItems) {
      if (item.type === 'section') {
        const maxCols = Math.max(1, Math.min(blockCols, item.span));
        h = Math.max(h, getSectionPreviewRows(item.section, maxCols));
      } else if (item.type === 'widget' && (item.rowSpan ?? 1) > 1) {
        h = Math.max(h, item.rowSpan ?? 1);
      }
      else{
        //nop
      };
    }
    map.set(r, h);
  }

  return map;
}

/**
 * Total content height in block-row height units for PREVIEW / read-only mode.
 *
 * Unlike `getContentHeightUnits`, this does NOT add trailing empty rows for
 * fully-covered widget rows, section rows, or multi-row widgets — those exist
 * only to support drag-drop interactions in the builder.
 */
export function getPreviewContentHeightUnits(items: BlockItem[], blockCols: number): number {
  if (items.length === 0) return 1;
  const heightMap = getPreviewBlockRowHeightMap(items, blockCols);
  return Math.max(1, Array.from(heightMap.values()).reduce((s, h) => s + h, 0));
}

/**
 * Total content height in block-row height units, including an optional +1
 * trailing empty row when:
 *   - the last occupied row is fully covered by widgets (span sum ≥ 3), OR
 *   - the last row contains a section (always needs a trailing row so the
 *     section's bottom resize handle is visible and grabbable).
 */
export function getContentHeightUnits(items: BlockItem[], blockCols: number): number {
  if (items.length === 0) return 1;

  const heightMap = getBlockRowHeightMap(items, blockCols);
  const maxRow = Math.max(...heightMap.keys());
  const totalH = Array.from(heightMap.values()).reduce((s, h) => s + h, 0);

  const lastRowItems = items.filter((i: BlockItem): boolean => (i.row ?? 1) === maxRow);
  const covered = lastRowItems.reduce<number>((s: number, i: BlockItem): number => s + i.span, 0);
  const hasSection = lastRowItems.some((i: BlockItem): boolean => i.type === 'section');
  const hasMultiRowWidget = lastRowItems.some(
    (i: BlockItem): boolean => i.type === 'widget' && (i.rowSpan ?? 1) > 1,
  );
  return covered >= blockCols || hasSection || hasMultiRowWidget ? totalH + 1 : Math.max(1, totalH);
}
